import { useMemo } from 'react'
import { Player } from '../types'
import { useAuthStore, usePlayerStore } from '../stores'
import {
  BarChart3, Target, TrendingUp, Clock, Shield, AlertTriangle,
  Activity, Crosshair, Zap
} from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, Line, Area, AreaChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts'

interface PlayerStatisticsProps {
  player?: Player
}

function PlayerStatistics({ player: playerProp }: PlayerStatisticsProps) {
  const { user } = useAuthStore()
  const { players } = usePlayerStore()

  const currentPlayer = useMemo(() => {
    if (playerProp) return playerProp
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [playerProp, user, players])

  const computedStats = useMemo(() => {
    if (!currentPlayer) return null

    const performances = currentPlayer.competition_performances || []
    const hasScoutData = performances.length > 0

    if (hasScoutData) {
      const totals = performances.reduce((acc, p) => ({
        goals: acc.goals + (p.goals || 0),
        assists: acc.assists + (p.assists || 0),
        mins: acc.mins + (p.minutes || 0),
        shots: acc.shots + (p.shots || 0),
        kp: acc.kp + (p.key_passes || 0),
        dr: acc.dr + (p.dribbles || 0),
        tackles: acc.tackles + (p.tackles || 0),
        apps: acc.apps + 1,
      }), { goals: 0, assists: 0, mins: 0, shots: 0, kp: 0, dr: 0, tackles: 0, apps: 0 })

      const totalPasses = totals.kp + (totals.mins / 10)
      const passAccuracy = totalPasses > 0 ? Math.round((totals.kp / totalPasses) * 100) : 0

      const pick = (scoutVal: number, playerVal: number | undefined) =>
        scoutVal > 0 ? scoutVal : (playerVal ?? 0)

      return {
        apps: pick(totals.apps, currentPlayer.apps),
        goals: pick(totals.goals, currentPlayer.goals),
        assists: pick(totals.assists, currentPlayer.assists),
        mins: pick(totals.mins, currentPlayer.mins),
        yellow_cards: currentPlayer.yellow_cards || 0,
        red_cards: currentPlayer.red_cards || 0,
        clean_sheets: currentPlayer.clean_sheets || 0,
        pass_accuracy: passAccuracy,
        tackles: pick(totals.tackles, currentPlayer.du),
        interceptions: currentPlayer.du,
        source: 'scout' as const,
      }
    }

    const totalPasses = currentPlayer.kp + (currentPlayer.mins / 10)
    const passAccuracy = totalPasses > 0 ? Math.round((currentPlayer.kp / totalPasses) * 100) : 0

    return {
      apps: currentPlayer.apps,
      goals: currentPlayer.goals,
      assists: currentPlayer.assists,
      mins: currentPlayer.mins,
      yellow_cards: currentPlayer.yellow_cards || 0,
      red_cards: currentPlayer.red_cards || 0,
      clean_sheets: currentPlayer.clean_sheets || 0,
      pass_accuracy: passAccuracy,
      tackles: currentPlayer.du,
      interceptions: currentPlayer.du,
      source: 'player' as const,
    }
  }, [currentPlayer])

  const goalsPerMonth = useMemo(() => {
    if (!currentPlayer) return []

    const monthData: Record<string, { goals: number; assists: number }> = {}
    const performances = currentPlayer.competition_performances || []
    const matchHistory = currentPlayer.match_history || []

    const dataSource = performances.length > 0 ? performances : matchHistory

    dataSource.forEach((record: any) => {
      const date = record.appearance_date || record.date
      if (!date) return
      const d = new Date(date)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      if (!monthData[key]) monthData[key] = { goals: 0, assists: 0 }
      monthData[key].goals += record.goals || record.stats?.goals || 0
      monthData[key].assists += record.assists || record.stats?.assists || 0
    })

    return Object.entries(monthData)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString('en', { month: 'short' }),
        goals: data.goals,
        assists: data.assists,
      }))
  }, [currentPlayer])

  const minutesData = useMemo(() => {
    if (!currentPlayer) return []

    const performances = currentPlayer.competition_performances || []
    const matchHistory = currentPlayer.match_history || []

    if (performances.length > 0) {
      return performances.slice(-10).map((p, i) => ({
        match: `Match ${i + 1}`,
        opponent: p.opponent || 'TBD',
        minutes: p.minutes || 0,
      }))
    }

    return matchHistory.slice(-10).map((m, i) => ({
      match: `Match ${i + 1}`,
      opponent: m.opponent || 'TBD',
      minutes: m.stats?.mins || 0,
    }))
  }, [currentPlayer])

  const performanceTrend = useMemo(() => {
    if (!currentPlayer) return []

    const monthData: Record<string, { goals: number; assists: number; rating: number; count: number }> = {}
    const performances = currentPlayer.competition_performances || []

    performances.forEach((p) => {
      const date = p.appearance_date
      if (!date) return
      const d = new Date(date)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      if (!monthData[key]) monthData[key] = { goals: 0, assists: 0, rating: 0, count: 0 }
      monthData[key].goals += p.goals || 0
      monthData[key].assists += p.assists || 0
      monthData[key].rating += p.rating || 0
      monthData[key].count += 1
    })

    return Object.entries(monthData)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString('en', { month: 'short' }),
        goals: data.goals,
        assists: data.assists,
        rating: data.count > 0 ? Math.round((data.rating / data.count) * 10) / 10 : 0,
      }))
  }, [currentPlayer])

  if (!currentPlayer || !computedStats) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
        <BarChart3 className="mx-auto text-gray-500 mb-4" size={48} />
        <h3 className="text-lg font-semibold text-white">No Statistics Available</h3>
        <p className="text-gray-400 mt-2">Your match statistics will appear here once data is recorded.</p>
      </div>
    )
  }

  const stats = [
    { label: 'Appearances', value: computedStats.apps, icon: BarChart3, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Goals', value: computedStats.goals, icon: Target, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Assists', value: computedStats.assists, icon: TrendingUp, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Minutes Played', value: computedStats.mins, icon: Clock, color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { label: 'Yellow Cards', value: computedStats.yellow_cards, icon: AlertTriangle, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { label: 'Red Cards', value: computedStats.red_cards, icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10' },
    { label: 'Clean Sheets', value: computedStats.clean_sheets, icon: Shield, color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
    { label: 'Pass Accuracy', value: `${computedStats.pass_accuracy}%`, icon: Crosshair, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
    { label: 'Tackles', value: computedStats.tackles, icon: Zap, color: 'text-pink-500', bg: 'bg-pink-500/10' },
    { label: 'Interceptions', value: computedStats.interceptions, icon: Activity, color: 'text-teal-500', bg: 'bg-teal-500/10' },
  ]

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Source Badge */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Statistics</h2>
          <p className="text-sm text-gray-400 mt-1">
            {computedStats.source === 'scout'
              ? 'Data sourced from scout/club entries'
              : 'Self-reported statistics'}
          </p>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${
          computedStats.source === 'scout'
            ? 'bg-green-500/10 text-green-400 border border-green-500/20'
            : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
        }`}>
          {computedStats.source === 'scout' ? 'Scout Verified' : 'Self-Reported'}
        </span>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {stats.map((item) => (
          <div key={item.label} className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-400 text-[10px] font-black uppercase tracking-widest">{item.label}</h3>
              <div className={`w-8 h-8 rounded-xl ${item.bg} flex items-center justify-center`}>
                <item.icon size={16} className={item.color} />
              </div>
            </div>
            <p className="text-3xl font-black text-white">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals Per Month */}
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target size={16} className="text-green-500" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Goals & Assists Per Month</h3>
          </div>
          {goalsPerMonth.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={goalsPerMonth} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={10} />
                  <YAxis stroke="#64748b" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#161b22',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#fff',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="goals" fill="#7cff01" radius={[4, 4, 0, 0]} name="Goals" />
                  <Bar dataKey="assists" fill="#60a5fa" radius={[4, 4, 0, 0]} name="Assists" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">No monthly data yet</p>
            </div>
          )}
        </div>

        {/* Minutes Played */}
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} className="text-orange-500" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Minutes Played</h3>
          </div>
          {minutesData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={minutesData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="match" stroke="#64748b" fontSize={10} />
                  <YAxis stroke="#64748b" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#161b22',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#fff',
                    }}
                    formatter={(value: any, _name: any, props: any) => [`${value}'`, props.payload.opponent]}
                  />
                  <Bar dataKey="minutes" fill="#f97316" radius={[4, 4, 0, 0]} name="Minutes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">No match data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Performance Trend */}
      <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-purple-500" />
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Performance Trend</h3>
        </div>
        {performanceTrend.length > 1 ? (
          <div className="h-72">
            <ResponsiveContainer width="100%" height={288}>
              <AreaChart data={performanceTrend} margin={{ top: 5, right: 20, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorGoals" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7cff01" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#7cff01" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorAssists" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={10} />
                <YAxis stroke="#64748b" fontSize={10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#161b22',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    color: '#fff',
                  }}
                />
                <Legend />
                <Area type="monotone" dataKey="goals" stroke="#7cff01" fillOpacity={1} fill="url(#colorGoals)" strokeWidth={2} name="Goals" />
                <Area type="monotone" dataKey="assists" stroke="#60a5fa" fillOpacity={1} fill="url(#colorAssists)" strokeWidth={2} name="Assists" />
                <Line type="monotone" dataKey="rating" stroke="#fbbf24" strokeWidth={2} dot={{ fill: '#fbbf24', strokeWidth: 2 }} name="Rating" yAxisId={0} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-72 flex items-center justify-center">
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Need at least 2 months of data for trend</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlayerStatistics
